import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { RequestStatus } from '../../../shared/api/types'
import type { RequestsPresetFilter } from '../../../shared/api/requests.api'

const PRESETS: RequestsPresetFilter[] = [
  'active',
  'burning',
  'stale',
  'stale14',
  'overdue',
  'overdue_sales',
  'overdue_procurement',
  'due_soon',
  'due_overdue',
]

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

/**
 * URL-фильтры списка заявок: одиночный статус, набор статусов (CSV), менеджер,
 * пресет с дашбордов и диапазон даты создания (клик по KPI-карточке
 * «Аналитика → Сводка»). Невалидные значения молча отбрасываются.
 */
export function useRequestsUrlFilters() {
  const [searchParams, setSearchParams] = useSearchParams()

  const status = (searchParams.get('status') ?? '') as RequestStatus | ''
  const statuses = searchParams.get('statuses') ?? ''
  const managerId = searchParams.get('managerId') ?? ''
  const presetRaw = searchParams.get('filter')
  const preset =
    presetRaw && PRESETS.includes(presetRaw as RequestsPresetFilter)
      ? (presetRaw as RequestsPresetFilter)
      : null
  const createdFromRaw = searchParams.get('createdFrom') ?? ''
  const createdToRaw = searchParams.get('createdTo') ?? ''
  const createdFrom = DATE_RE.test(createdFromRaw) ? createdFromRaw : ''
  const createdTo = DATE_RE.test(createdToRaw) ? createdToRaw : ''
  const deadlineToRaw = searchParams.get('deadlineTo') ?? ''
  const deadlineTo = DATE_RE.test(deadlineToRaw) ? deadlineToRaw : ''

  /** Есть ли активные URL-фильтры (для кнопки «× Сбросить фильтр»). */
  const hasUrlFilters = !!(
    managerId ||
    statuses ||
    preset ||
    createdFrom ||
    createdTo ||
    deadlineTo
  )

  const resetUrlFilters = useCallback(() => setSearchParams({}), [setSearchParams])

  /** Смена одиночного статуса в селекте шапки: сохраняем сужение по менеджеру
   *  и период создания (если пришли из аналитики), но сбрасываем набор
   *  статусов/пресет — выбран одиночный статус. */
  const setStatusFilter = useCallback(
    (val: string) => {
      const next: Record<string, string> = {}
      if (managerId) next.managerId = managerId
      if (createdFrom) next.createdFrom = createdFrom
      if (createdTo) next.createdTo = createdTo
      if (val) next.status = val
      setSearchParams(next)
    },
    [managerId, createdFrom, createdTo, setSearchParams],
  )

  return {
    status,
    statuses,
    managerId,
    preset,
    createdFrom,
    createdTo,
    deadlineTo,
    hasUrlFilters,
    resetUrlFilters,
    setStatusFilter,
  }
}
